/* jshint browser:true */
import DomUtils from '../dom-utils';

/**
 * Adds floating button to scroll to the next section component.
 */
export default class SectionScroll {
  /**
   * Constructor.
   */
  constructor() {
    this.options = {
      showSectionTitles: false,
    };

    this.classNames = {
      section: 'section',
      button: 'js-section-scroll__button',
      active: 'js-section-scroll--active',
      last: 'js-section-scroll--last',
      showTitles: 'js-section-scroll--show-title',
      topElementSelector: '.page-wrapper',
    };

    this.text = {
      backToTop: 'Back to Top',
      nextSection: 'Next Section',
    };

    this.nextSection = {
      el: null,
      index: -1,
      title: this.text.nextSection,
    };
  }

  /**
   * Initializes feature.
   * @param {string} selector CSS selector for element that contains sections.
   * @param {Object} options Optional settings to override the defaults.
   */
  init(selector, options = {}) {
    const scopeEl = document.querySelector(selector);

    if (!scopeEl) {
      // No feature to initialize. Fail silently.
      return;
    }

    this.options = Object.assign(this.options, options);
    this.setup(scopeEl);
  }

  /**
   * Sets up the feature.
   * @param {HTMLElement} wrapper The root element to which the feature will be
   *        applied.
   */
  setup(wrapper) {
    const sections = wrapper.querySelectorAll('.' + this.classNames.section);

    if (sections.length) {
      // Add button and watch for scroll changes
      const buttonEl = this.addButton(sections);

      // Add classes to HTML element for styling
      const htmlEl = document.querySelector('html');
      const htmlClassNames = [this.classNames.active];

      if (this.options.showSectionTitles) {
        htmlClassNames.push(this.classNames.showTitles);
      }

      htmlEl.classList.add(...htmlClassNames);

      // Set button width to width of longest section name
      if (this.options.showSectionTitles) {
        requestAnimationFrame(() => {
          const maxWidth = Array.from(sections).reduce((maxWidth, section) => {
            buttonEl.innerText =
              section.dataset.sectionTitle || this.text.nextSection;

            const { width } = DomUtils.getElementDimensions(buttonEl);

            if (width > maxWidth) {
              maxWidth = width;
            }

            return maxWidth;
          }, 0);

          // Set width (add 1 pixel since offsetWidth may round down)
          buttonEl.style.width = maxWidth + 1 + 'px';
        });
      }

      // Update button when scrolling occurs
      requestAnimationFrame(() => {
        this.watchScroll(sections, buttonEl);
      });
    }
  }

  /**
   * Sets up the scroll button.
   *
   * @return {HTMLElement} The button element.
   */
  addButton() {
    const bodyEl = document.querySelector('body');
    const buttonEl = document.createElement('button');

    // Attributes
    buttonEl.setAttribute('type', 'button');
    buttonEl.className = this.classNames.button;

    if (this.options.showSectionTitles) {
      buttonEl.appendChild(document.createTextNode(this.nextSection.title));
    } else {
      buttonEl.setAttribute('aria-label', this.nextSection.title);
    }

    // Event listeners
    buttonEl.addEventListener('click', () => {
      window.requestAnimationFrame(() => {
        // Scroll section into view
        this.nextSection.el.scrollIntoView({
          behavior: 'smooth',
        });
      });
    });

    // Append
    bodyEl.appendChild(buttonEl);

    return buttonEl;
  }

  /**
   * Watches for scroll changes and sets the section to which the scroll button
   * will scroll.
   * @param {HTMLElement[]} sections The section elements to which to scroll.
   * @param {HTMLButtonElement} buttonEl The scroll button element.
   */
  watchScroll(sections, buttonEl) {
    const parallaxRoot = document.querySelector('.parallax-layout');
    const rootEl =
      window.RavenPatternLibrary || !parallaxRoot ? null : parallaxRoot;

    const observerOptions = {
      root: rootEl,
      rootMargin: '0px',
      threshold: [0.1, 0.2, 0.3, 0.5, 0.8],
    };

    const sectionCount = sections.length;

    // Add observers for each section
    sections.forEach((section, index) => {
      const observer = new IntersectionObserver((entries) => {
        // Find section at top of viewport
        const viewportHeight = window.innerHeight;
        let nextSectionIndex = 0;

        for (let n = sectionCount - 1; n >= 0; n--) {
          const rect = sections[n].getBoundingClientRect();

          if (rect.top - viewportHeight * 0.75 <= 0) {
            nextSectionIndex = n + 1;
            break;
          }
        }

        // Set next section
        if (nextSectionIndex === sectionCount) {
          this.nextSection = {
            el: document.querySelector(this.classNames.topElementSelector),
            index: 0,
            title: this.text.backToTop,
          };
        } else {
          this.nextSection = {
            el: sections[nextSectionIndex],
            index: nextSectionIndex,
            title:
              sections[nextSectionIndex].dataset.sectionTitle ||
              this.text.nextSection,
          };
        }

        // Update button
        if (!this.nextSection.el) {
          buttonEl.setAttribute('hidden', 'hidden');
        } else {
          buttonEl.removeAttribute('hidden');

          if (this.options.showSectionTitles) {
            buttonEl.innerText = this.nextSection.title;
          }
        }

        if (nextSectionIndex === sectionCount) {
          buttonEl.classList.add(this.classNames.last);
        } else {
          buttonEl.classList.remove(this.classNames.last);
        }
      }, observerOptions);

      observer.observe(section);
    });
  }
}
